import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { dietService } from '../../services/dietService';
import { dietPriceService } from '../../services/dietPriceService';
import { Diet, DietPriceRow } from '../../types/diet';

interface Props {
  priceId: string;
  row?: DietPriceRow;
  onClose: () => void;
  onSuccess: () => void;
}

const DietPriceModal: React.FC<Props> = ({ priceId, row, onClose, onSuccess }) => {
  const isEditing = !!row?.id;
  const [dietId, setDietId] = useState<string>(row?.dietId || '');
  const [pricePerDay, setPricePerDay] = useState<string>(row?.pricePerDay?.toString() || '');
  const [currency, setCurrency] = useState<string>(row?.currency || 'USD');
  const [periodDays, setPeriodDays] = useState<string>(row?.periodDays?.toString() || '');
  const [totalPrice, setTotalPrice] = useState<string>(row?.totalPrice?.toString() || '');
  const [notes, setNotes] = useState<string>(row?.notes || '');
  const [status, setStatus] = useState<'ACTIVE'|'INACTIVE'>(row?.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');

  const { data: dietsData } = useQuery({
    queryKey: ['diets', 'for-select'],
    queryFn: () => dietService.getDiets({ page: 1, limit: 1000, status: 'ACTIVE' as any })
  });
  const dietOptions: Diet[] = useMemo(() => (dietsData?.diets || []), [dietsData]);

  // auto-calc total when pricePerDay or periodDays change (unless user manually changed total)
  useEffect(() => {
    const p = Number(pricePerDay || 0);
    const d = Number(periodDays || 0);
    if (!isNaN(p) && !isNaN(d)) {
      setTotalPrice((p * d).toFixed(2));
    }
  }, [pricePerDay, periodDays]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Partial<DietPriceRow> = {
        id: row?.id,
        dietId,
        pricePerDay: Number(pricePerDay || 0),
        currency: currency || 'USD',
        periodDays: Number(periodDays || 0),
        totalPrice: Number(totalPrice || 0),
        notes: notes || undefined,
        status,
      };
      return dietPriceService.bulkUpsert(priceId, [payload]);
    },
    onSuccess: () => {
      onSuccess();
    }
  });

  const canSave = priceId && dietId && (pricePerDay !== '') && (periodDays !== '');

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-4 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-2">
                  <Dialog.Title className="text-lg font-semibold">{isEditing ? 'Edit Diet Price' : 'Create Diet Price'}</Dialog.Title>
                  <button onClick={onClose} className="p-2 rounded hover:bg-gray-100"><XMarkIcon className="w-5 h-5"/></button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Diet *</label>
                    <select className="form-input h-11 text-base" value={dietId} onChange={e=>setDietId(e.target.value)}>
                      <option value="">Select Diet</option>
                      {dietOptions.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Currency *</label>
                    <input className="form-input h-11 text-base" value={currency} onChange={e=>setCurrency(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Price/Day *</label>
                    <input type="number" step="0.01" className="form-input h-11 text-base" value={pricePerDay} onChange={e=>setPricePerDay(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Period (Days) *</label>
                    <input type="number" className="form-input h-11 text-base" value={periodDays} onChange={e=>setPeriodDays(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Total Price</label>
                    <input type="number" step="0.01" className="form-input h-11 text-base" value={totalPrice} onChange={e=>setTotalPrice(e.target.value)} />
                  </div>
                  <div>
                    <label className="form-label">Status *</label>
                    <select className="form-input h-11 text-base" value={status} onChange={e=>setStatus(e.target.value as any)}>
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Notes</label>
                    <input className="form-input h-11 text-base" value={notes} onChange={e=>setNotes(e.target.value)} />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="secondary" onClick={onClose}>Cancel</Button>
                  <Button onClick={() => saveMutation.mutate()} disabled={!canSave} loading={saveMutation.isPending}>Save</Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DietPriceModal;

